import { List, Map } from 'immutable';

import { ReadingIntentionsKey } from '®shared/annotations';
import {
  BibliographicResourceType,
  isOnlineResourcePreview,
} from '®shared/bibliographic-resource';
import {
  ContainedTypeAndId,
  ContainerData,
  containerKey,
} from '®shared/container-structure/state';
import { NO_IMPORTANCE_SET } from '®shared/list-item/importance/importance.constant';
import { OnlineResourcesByItemId } from '®shared/reading-list';
import { EditableItem } from '®shared/reading-list/models';
import {
  ItemCountByQuickFilters,
  QuickFilter,
  getCourseReserveStatusFilterForItem,
  getDigitisationStatusFilterForItem,
} from '®shared/reading-list/quick-filters';
import { CourseReserveFilterStatus } from '®shared/reading-list/quick-filters/course-reserve-status-filter-and-label';
import { DigitisationFilterStatus } from '®shared/reading-list/quick-filters/digitisation-status-filter-and-label';
import {
  ItemCountByResourceTypeFilter,
  ResourceTypeFilter,
} from '®shared/reading-list/resource-type-filter';
import {
  ReadingIntentionsByResourceId,
  StudyNotesByResourceId,
} from '®shared/user-annotations';

import { ListItemsById } from './list-items.state';

type ItemOrItemReference =
  | EditableItem
  | (ContainedTypeAndId & { type: 'items' });

export type FilterPredicate = (item: EditableItem) => boolean;

/**
 * Returns true if the `actual` value contains any string that has
 * `expected` as a substring.
 *
 * This works in a "depth-first" fashion, recursively inspecting the elements
 * of arrays, and the values of objects.
 *
 * @param {any} actual - the actual value to match against
 * @param {string} expected - the search pattern to find
 * @returns {Boolean} - true if `expected` is a substring of a string from `actual`
 */
function someDeepMatch(actual: any, expected: string): boolean {
  if (!actual) {
    return false;
  } else if (typeof actual === 'string') {
    return actual.toLowerCase().indexOf(expected) !== -1;
  } else if (Array.isArray(actual)) {
    return actual.some(actualElement => someDeepMatch(actualElement, expected));
  } else if (typeof actual === 'object') {
    return Object.keys(actual).some(key =>
      someDeepMatch(actual[key], expected),
    );
  }

  return false;
}

/**
 * Filters a list of entries against a pattern, to only those entries that:
 *
 * - are an `EditableItem` object, whose `resource` field contains a string matching the pattern
 * - are any other kind of element (`sections`, `section_openers`, `section_closers`)
 *
 * @param {List<ItemOrItemReference>} items - a list of all items and item-references
 * @param {?string} filterText - optional textual search pattern
 * @param {?QuickFilter} quickFilter - optional quick filter
 * @param {?ResourceTypeFilter} resourceTypeFilter - optional resource type filter
 * @returns {List<ItemOrItemReference>} - those entries that are `non-items` or matching `items`
 */
function filterItems(
  items: List<ItemOrItemReference>,
  filterText: string,
  quickFilter: QuickFilter,
  resourceTypeFilter: ResourceTypeFilter,
): List<ItemOrItemReference> {
  const isAnyFilterApplied = quickFilter || resourceTypeFilter || filterText;
  if (!items || !isAnyFilterApplied) {
    return items;
  }

  // Check that we have more than just the `id` and `type` which is just an item reference.
  const isEditableItem = (itemOrId: ItemOrItemReference) =>
    Object.keys(itemOrId).length > 2;

  const isNotResolvedItemOrPredicateIsTrue = (
    itemOrId: ItemOrItemReference,
    predicate: FilterPredicate,
  ) => (isEditableItem(itemOrId) ? predicate(itemOrId) : true);

  const isTextFilterMatched = (item: EditableItem, pattern: string) =>
    typeof pattern === 'string' && pattern.trim()
      ? someDeepMatch(item.resource, pattern.toLowerCase())
      : true;

  const isQuickFilterMatched: FilterPredicate = quickFilter
    ? quickFilter.predicate.bind(quickFilter)
    : () => true;
  const isResourceTypeFilterMatch: FilterPredicate = resourceTypeFilter
    ? resourceTypeFilter.predicate.bind(resourceTypeFilter)
    : () => true;

  const areAllFiltersMatched: FilterPredicate = (item: EditableItem) =>
    isQuickFilterMatched(item) &&
    isResourceTypeFilterMatch(item) &&
    isTextFilterMatched(item, filterText);

  const isNotResolvedItemOrBothFiltersMatch = (item: ItemOrItemReference) =>
    isNotResolvedItemOrPredicateIsTrue(item, areAllFiltersMatched);
  return items.filter(isNotResolvedItemOrBothFiltersMatch);
}

const incrementCount = (count: number): number => count + 1;

function reduceItemCountByResourceType(
  itemCount: Map<BibliographicResourceType, number>,
  item: EditableItem,
) {
  const resource = item.resource;
  if (resource && resource.resource_type) {
    return itemCount.update(resource.resource_type, 0, incrementCount);
  } else {
    return itemCount;
  }
}

function reduceItemCountByImportance(
  itemCount: Map<string, number>,
  item: EditableItem,
) {
  if (item.importance && item.importance.id) {
    return itemCount.update(item.importance.id, 0, incrementCount);
  } else {
    return itemCount.update(NO_IMPORTANCE_SET, 0, incrementCount);
  }
}

function getItemCountByDigitisationStatus(
  itemCount: Map<DigitisationFilterStatus, number>,
  item: EditableItem,
) {
  const digitisationStatusFilter = getDigitisationStatusFilterForItem(item);

  if (digitisationStatusFilter) {
    return itemCount.update(digitisationStatusFilter, 0, incrementCount);
  } else {
    return itemCount;
  }
}

function getItemCountByCourseReserveStatus(
  itemCount: Map<CourseReserveFilterStatus, number>,
  item: EditableItem,
) {
  const courseReserveStatusFilter = getCourseReserveStatusFilterForItem(item);
  if (courseReserveStatusFilter) {
    return itemCount.update(courseReserveStatusFilter, 0, incrementCount);
  } else {
    return itemCount;
  }
}

function reduceItemCountByOnlineOrPhysicalResourceType(
  itemCount: Map<boolean, number>,
  item: EditableItem,
  onlineResourcesByItemId: OnlineResourcesByItemId,
) {
  const onlineResource = onlineResourcesByItemId.get(item.id);
  const onlineResourceFilterValue =
    onlineResource && !isOnlineResourcePreview(onlineResource) ? true : false;
  return itemCount.update(onlineResourceFilterValue, 0, incrementCount);
}

function reduceItemCountByStudyNote(
  itemCount: Map<boolean, number>,
  item: EditableItem,
  studyNotesByResourceId: StudyNotesByResourceId,
) {
  const resource = item.resource;
  const studyNote = resource && studyNotesByResourceId[resource.id];
  const studyNoteFilterValue = studyNote ? true : false;
  return itemCount.update(studyNoteFilterValue, 0, incrementCount);
}

function reduceItemCountByReadingIntention(
  itemCount: Map<ReadingIntentionsKey, number>,
  item: EditableItem,
  readingIntentionsByResourceId: ReadingIntentionsByResourceId,
) {
  const resource = item.resource;
  const intention = resource && readingIntentionsByResourceId[resource.id];
  const readingIntentionsFilterValue =
    intention && intention.status
      ? intention.status
      : ReadingIntentionsKey.Undecided;
  return itemCount.update(readingIntentionsFilterValue, 0, incrementCount);
}

export function getItemCountByQuickFilters(
  containerData: ContainerData,
  itemsById: ListItemsById,
  onlineResourcesByItemId: OnlineResourcesByItemId,
  studyNotesByResourceId: StudyNotesByResourceId,
  readingIntentionsByResourceId: ReadingIntentionsByResourceId,
): ItemCountByQuickFilters {
  if (!itemsById || !containerData || containerData.allKeys.isEmpty()) {
    return {
      totalItems: 0,
      itemCountByImportance: Map<string, number>(),
      itemCountByPhysicalOrOnlineResourceType: Map<boolean, number>(),
      itemCountByDigitisationStatus: Map<DigitisationFilterStatus, number>(),
      itemCountByCourseReserveStatus: Map<CourseReserveFilterStatus, number>(),
      itemCountByStudyNote: Map<boolean, number>(),
      itemCountByReadingIntention: Map<ReadingIntentionsKey, number>(),
      itemCountForHasLibraryNote: 0,
    };
  }

  const allItems: List<ItemOrItemReference> = containerData.allKeys
    .toList()
    .filter(entry => entry.type === 'items')
    .map(itemId => itemsById.get(itemId.id) || itemId);

  const filterableItems = allItems
    .filter(filterExcludeNonResourceItems)
    .filter(filterExcludePages);

  const itemCountByImportance = filterableItems.reduce(
    reduceItemCountByImportance,
    Map<string, number>(),
  );

  const itemCountByDigitisationStatus = filterableItems.reduce(
    getItemCountByDigitisationStatus,
    Map<DigitisationFilterStatus, number>(),
  );

  const itemCountByCourseReserveStatus = filterableItems.reduce(
    getItemCountByCourseReserveStatus,
    Map<CourseReserveFilterStatus, number>(),
  );

  const itemCountByPhysicalOrOnlineResourceType = filterableItems.reduce(
    (itemCount, item: EditableItem) =>
      reduceItemCountByOnlineOrPhysicalResourceType(
        itemCount,
        item,
        onlineResourcesByItemId,
      ),
    Map<boolean, number>(),
  );

  const itemCountByStudyNote = filterableItems.reduce(
    (itemCount, item: EditableItem) =>
      reduceItemCountByStudyNote(itemCount, item, studyNotesByResourceId),
    Map<boolean, number>(),
  );

  const itemCountByReadingIntention = filterableItems.reduce(
    (itemCount, item: EditableItem) =>
      reduceItemCountByReadingIntention(
        itemCount,
        item,
        readingIntentionsByResourceId,
      ),
    Map<ReadingIntentionsKey, number>(),
  );

  const itemCountForHasLibraryNote = filterableItems.reduce(
    (itemCount, item: EditableItem) => {
      if (item.library_note) {
        return itemCount + 1;
      }
      return itemCount;
    },
    0,
  );

  return {
    totalItems: filterableItems.count(),
    itemCountByImportance,
    itemCountByPhysicalOrOnlineResourceType,
    itemCountByDigitisationStatus,
    itemCountByCourseReserveStatus,
    itemCountByStudyNote,
    itemCountByReadingIntention,
    itemCountForHasLibraryNote,
  };
}

export function getItemCountForResourceTypeFilter(
  containerData: ContainerData,
  itemsById: ListItemsById,
): ItemCountByResourceTypeFilter {
  if (!itemsById || !containerData || containerData.allKeys.isEmpty()) {
    return {
      totalItems: 0,
      itemCountByResourceType: Map<BibliographicResourceType, number>(),
    };
  }

  const allItems: List<ItemOrItemReference> = containerData.allKeys
    .toList()
    .filter(entry => entry.type === 'items')
    .map(itemId => itemsById.get(itemId.id) || itemId);

  const filterableItems = allItems.filter(filterExcludeNonResourceItems);

  const itemCount = filterableItems.reduce(
    reduceItemCountByResourceType,
    Map<BibliographicResourceType, number>(),
  );

  return {
    totalItems: filterableItems.count(),
    itemCountByResourceType: itemCount,
  };
}

const filterExcludeNonResourceItems = (item: ItemOrItemReference) =>
  'resource' in item;

const filterExcludePages = (item: ItemOrItemReference) =>
  'resource' in item && item.resource.isNonPageResource();

/**
 * Filters a list of entries against a textual- or quick-filter, to only those entries that:
 *
 * - are items that match the filter conditions
 * - are a section or marker that contains at least one item matching the filter conditions.
 *
 * @param {ContainerData} containerData - the current container data
 * @param {ListItemsById} itemsById - current list items, keyed by ID
 * @param {?string} filterText - optional textual search pattern
 * @param {?QuickFilter} quickFilter - optional quick filter
 * @param {?resourceTypeFilter} resourceTypeFilter - optional resource type filter
 * @returns {Map<string, boolean>} - a map of entry ID -> visibility flag
 */
export function filterEntryIds(
  containerData: ContainerData,
  itemsById: ListItemsById,
  filterText?: string,
  quickFilter?: QuickFilter,
  resourceTypeFilter?: ResourceTypeFilter,
): Map<string, boolean> {
  if (!containerData) {
    return Map<string, boolean>();
  }

  const { rootKey, allKeys, pathReadouts } = containerData;
  if (allKeys.isEmpty()) {
    return Map<string, boolean>();
  }

  // we filter to items that match the specified filters, or are yet to load
  const allItems: List<ItemOrItemReference> = allKeys
    .toList()
    .filter(entry => entry.type === 'items')
    .map(itemId => itemsById.get(itemId.id) || itemId);

  const visibleItems = filterItems(
    allItems,
    filterText,
    quickFilter,
    resourceTypeFilter,
  );
  const areAllItemsVisible = visibleItems.size === allItems.size;

  // if no items are filtered, everything is visible
  if (areAllItemsVisible) {
    return allKeys
      .reduce(
        (soFar, entry: ContainedTypeAndId) =>
          soFar.set(containerKey(entry), true),
        Map<string, boolean>(),
      )
      .remove(rootKey);
  }

  // For each visible item, fetch its ancestor path, and mark all ancestors visible
  const visibleKeys: Map<string, boolean> = visibleItems.reduce(
    (visibleKeysSoFar, visibleItem) => {
      const itemAncestors = pathReadouts.get(containerKey(visibleItem));
      return itemAncestors.reduce(
        (visibleItemAncestorsSoFar, itemAncestor) =>
          visibleItemAncestorsSoFar.set(itemAncestor, true),
        visibleKeysSoFar,
      );
    },
    Map<string, boolean>(),
  );

  // default state is hidden; override to show
  const allKeysHiddenByDefault: Map<string, boolean> = allKeys.reduce(
    (soFar, entry: ContainedTypeAndId) => soFar.set(containerKey(entry), false),
    Map<string, boolean>(),
  );

  // combine all-hidden with matches-shown; root container is never filtered
  return allKeysHiddenByDefault.merge(visibleKeys).remove(rootKey);
}
